import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { isUnreachable } from '../../utils/collectionRecord';
import { resendAsNew } from '../../utils/fieldBatches';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { getApiBase } from '../../utils/serverConfig';
import { viewingAs } from '../../utils/viewAs';
import { formatMoment, pageStyles as ps } from './fieldFormat';
import PhotoThumb from '../PhotoThumb';

/** Who may bring a deleted record back (routes/soft_delete.py RESTORE_ROLES). */
export const RESTORE_ROLES = ['collection_manager', 'system_admin'];
export const mayRestore = user => (user?.roles || []).some(r => RESTORE_ROLES.includes(r));

/** Why a record this phone sent is not on the server: deleted there, made
 *  through another server address, or simply not found. */
export function goneReason(gone, batch, base = getApiBase()) {
  if (gone?.deleted) return 'deleted';
  if (batch?.serverBase && batch.serverBase !== base) return 'otherServer';
  if (gone && gone.known === false) return 'unknown';
  return 'missing';
}

/**
 * A record this phone sent that the server no longer has
 * (GET /collections/{id} answered 404). The phone's copy stays visible --
 * what was written down, the photos still on it (else where they were
 * stored, which a soft delete keeps), the voice notes -- and the collector
 * chooses the way on:
 *
 *   - Restore: a deleted record, by whoever may restore (a collection manager
 *     or a system administrator); it then goes on as the outbox left it.
 *   - Send again as a new record: one this server never had or removed for
 *     good, made by this phone's outbox. Its specimen code is kept only while
 *     this server still holds that code as a specimen's; otherwise a new one
 *     is minted and the label has to be rewritten.
 *   - Remove from this phone: the copy goes, after a second tap.
 *
 * Nothing is offered while an administrator views the app as another role:
 * the phone's store and the server refuse every write then.
 */
export default function FieldGonePanel({ sampleId, batch, gone, store, outbox, userId, user, onRestored, onRemoved }) {
  const { t } = useTranslation(['field', 'common', 'collections']);
  const [photos, setPhotos] = useState([]);
  const [audioCount, setAudioCount] = useState(0);
  const [onDevice, setOnDevice] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const reason = goneReason(gone, batch);
  const viewing = !!viewingAs();

  // The phone's photos: their files where it still has them, else where they
  // were stored. Object URLs let go with the panel.
  const photoIds = (batch?.photos || []).map(p => p.id).join(',');
  useEffect(() => {
    let alive = true;
    const made = [];
    (async () => {
      const shown = [];
      const held = new Set();
      for (const p of (batch?.photos || []).filter(x => !x.lost)) {
        const blob = await store.getBlob(userId, p.id).catch(() => null);
        if (blob) {
          held.add(p.id);
          if (typeof URL.createObjectURL === 'function') {
            const url = URL.createObjectURL(blob);
            made.push(url);
            shown.push({ id: p.id, src: url, view: p.view });
          }
        } else if (p.s3_url) {
          shown.push({ id: p.id, src: resolvePhotoUrl(p.s3_url), view: p.view });
        }
      }
      let audio = 0;
      for (const a of (batch?.audioNotes || []).filter(x => !x.lost)) {
        if (await store.getBlob(userId, a.id).catch(() => null)) {
          held.add(a.id);
          audio += 1;
        }
      }
      if (!alive) return;
      setPhotos(shown);
      setAudioCount(audio);
      setOnDevice(held);
    })();
    return () => {
      alive = false;
      made.forEach(u => URL.revokeObjectURL(u));
    };
  }, [photoIds, store, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async () => {
    setBusy('restore');
    setMessage(null);
    try {
      await api.post(`/collections/${sampleId}/restore`, {});
      await store.update(userId, batch.id, current => ({
        ...current,
        gone: undefined,
        attention: null,
        // A record never submitted still has to be: the outbox does that.
        status: current.submitted ? 'sent' : 'pending',
        photoPasses: 0,
        serverErrors: 0,
      }));
      outbox.run();
      onRestored?.();
    } catch (e) {
      setMessage(isUnreachable(e) ? t('editor.offline') : t('editor.gone.restoreFailed'));
    } finally {
      setBusy('');
    }
  };

  const sendAgain = async () => {
    setBusy('send');
    setMessage(null);
    try {
      // A typed label is in the details and goes as it is. A minted code is
      // kept only while this server holds it as a specimen's.
      let keepCode = false;
      if (batch.specimenCode) {
        try {
          const found = await api.get('/lookup', { params: { code: batch.specimenCode } });
          keepCode = found?.data?.type === 'specimen';
        } catch (e) {
          if (isUnreachable(e)) throw e;
        }
      }
      await store.update(userId, batch.id, current => resendAsNew(current, { keepCode, onDevice }));
      outbox.run();
    } catch (e) {
      setMessage(isUnreachable(e) ? t('editor.offline') : t('editor.storeFailed'));
      setBusy('');
    }
  };

  const remove = async () => {
    setBusy('remove');
    try {
      await store.remove(userId, batch.id);
      onRemoved?.();
    } catch {
      setMessage(t('editor.storeFailed'));
      setBusy('');
    }
  };

  const details = batch?.details || {};
  const lines = [
    ['identification', [details.taxon_name, details.rank && details.rank !== 'unknown' ? t(`common:rank.${details.rank}`, { defaultValue: details.rank }) : '']
      .filter(Boolean).join(' · ')],
    ['notes', details.general_notes],
    ['host', details.host_species],
    ['substrate', details.substrate ? t(`collections:substrate.${details.substrate}`, { defaultValue: details.substrate }) : ''],
  ].filter(([, v]) => v);
  const lost = (batch?.photos || []).filter(p => !onDevice.has(p.id) && !p.lost).length;
  const canResend = reason !== 'deleted' && reason !== 'unknown' && !!batch?.createKey;
  const willMint = !!batch?.specimenCode;

  return (
    <section style={{ ...ps.section, borderColor: 'var(--c-danger-soft-2)' }} aria-labelledby="gone-title"
      data-testid="editor-gone" data-reason={reason}>
      <h2 id="gone-title" style={ps.h2}>{t('editor.gone.title')}</h2>
      <p style={ps.warn} role="alert" data-testid="editor-gone-why">
        {t(`editor.gone.why.${reason}`, {
          when: formatMoment(gone?.deletedAt) || '—',
          server: batch?.serverBase || '',
        })}
      </p>
      <p style={ps.hint}>{t('editor.gone.copyHelp')}</p>

      {lines.length > 0 && (
        <dl style={gs.dl} data-testid="editor-gone-details">
          {lines.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt style={ps.label}>{t(`editor.gone.fields.${k}`)}</dt>
              <dd style={gs.dd}>{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {photos.length > 0 && (
        <ul style={gs.grid} data-testid="editor-gone-photos">
          {photos.map((p, i) => (
            <li key={p.id} style={gs.cell}>
              <PhotoThumb photos={photos.map(x => ({ s3_url: x.src, photo_type: x.view, photo_id: x.id }))} index={i}
                style={{ width: '100%', height: '100%' }}
                label={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.view}`, { defaultValue: p.view }) })}>
                <img src={p.src} alt={t(`views.${p.view}`, { defaultValue: p.view })} style={gs.img}
                  loading="lazy" decoding="async" />
              </PhotoThumb>
            </li>
          ))}
        </ul>
      )}
      {photos.length === 0 && batch?.thumbnail && (
        <img src={batch.thumbnail} alt="" style={{ ...gs.img, width: 96, height: 96 }} />
      )}
      {audioCount > 0 && <p style={ps.hint}>{t('editor.gone.audio', { count: audioCount })}</p>}

      {message && <p style={ps.warn} role="alert" data-testid="editor-gone-message">{message}</p>}
      {viewing ? (
        <p style={ps.note} data-testid="editor-gone-viewing">{t('editor.gone.viewing')}</p>
      ) : (
        <div style={{ ...ps.row, flexDirection: 'column', alignItems: 'stretch' }}>
          {reason === 'deleted' && mayRestore(user) && (
            <button type="button" style={ps.primary} disabled={!!busy} onClick={restore}
              title={t('editor.gone.tip.restore')} data-testid="editor-gone-restore">
              {busy === 'restore' ? t('editor.saving') : t('editor.gone.restore')}
            </button>
          )}
          {reason === 'deleted' && !mayRestore(user) && (
            <p style={ps.hint} data-testid="editor-gone-ask">{t('editor.gone.askManager')}</p>
          )}
          {canResend && (
            <>
              <button type="button" style={ps.primary} disabled={!!busy} onClick={sendAgain}
                title={t('editor.gone.tip.sendAgain')} data-testid="editor-gone-send-again">
                {busy === 'send' ? t('editor.saving') : t('editor.gone.sendAgain')}
              </button>
              <p style={ps.hint}>
                {[willMint ? t('editor.gone.codeMayChange') : '',
                  lost ? t('editor.gone.notResendable', { count: lost }) : ''].filter(Boolean).join(' ')}
              </p>
            </>
          )}
          {!confirming ? (
            <button type="button" style={ps.secondary} disabled={!!busy} onClick={() => setConfirming(true)}
              title={t('editor.gone.tip.remove')} data-testid="editor-gone-remove">
              {t('editor.gone.remove')}
            </button>
          ) : (
            <div style={ps.row} role="group" aria-label={t('editor.gone.removeConfirm')}>
              <p style={{ ...ps.hint, flexBasis: '100%' }}>{t('editor.gone.removeConfirm')}</p>
              <button type="button" style={{ ...ps.secondary, color: 'var(--c-danger-4-fg)' }} disabled={!!busy}
                onClick={remove} title={t('editor.gone.tip.removeYes')} data-testid="editor-gone-remove-yes">
                {t('editor.gone.removeYes')}
              </button>
              <button type="button" style={ps.linkBtn} disabled={!!busy} onClick={() => setConfirming(false)}
                title={t('editor.gone.tip.keep')} data-testid="editor-gone-keep">
                {t('editor.gone.keep')}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const gs = {
  dl: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 12px', margin: 0 },
  dd: { margin: 0, fontSize: 15, color: 'var(--c-text-2-fg)', overflowWrap: 'anywhere' },
  grid: {
    listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8,
    gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
  },
  cell: { aspectRatio: '1 / 1', borderRadius: 8, overflow: 'hidden', background: 'var(--c-bg-subtle-6)' },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 8 },
};
